import React from "react";
import { deriveCampaignData } from "../common/util";
import { ReactComponent as MatchIcon } from '../../../assets/images/svgs/match_number.svg';
import { prettyCount } from "../../../utils/format";
import { Table, TableColumnsType } from "antd";
import styled from "styled-components";
import { Bar, BarChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from "recharts";

interface CampaignDataRecord {
  name: string;
  contacted: number;
  recipient: number;
  opened: number;
  clicked: number;
  replied: number;
  interested: number;
  [key: string]: any;
}

const getPercent = (value: number, recipient: number): number => {
  if (!recipient) {
    return 0;
  }

  return Math.floor((value * 100) / recipient);
};

const GetTableColumns = (): TableColumnsType<CampaignDataRecord> => {
  return [
    {
      title: 'Time Period',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Recipients',
      dataIndex: 'recipient',
      key: 'recipient',
      align: 'center',
      className: 'capitalize-text',
      render: (text: any, record: any) => (<>
        <MatchIcon style={{ marginRight: '2px' }} />{`${record.recipient}`}
      </>)
    },
    {
      title: 'Opened',
      dataIndex: 'opened',
      key: 'opened',
      align: 'center',
      className: 'capitalize-text',
      render: (text: any, record: any) => (<>
        {record.opened} ({`${getPercent(record.opened, record.recipient)}%`})
      </>)
    },
    {
      title: 'clicked',
      dataIndex: 'clicked',
      key: 'clicked',
      align: 'center',
      className: 'capitalize-text',
      render: (text: any, record: any) => (<>
        {record.clicked} ({`${getPercent(record.clicked, record.recipient)}%`})
      </>)
    },
    {
      title: 'replied',
      dataIndex: 'replied',
      key: 'replied',
      align: 'center',
      className: 'capitalize-text',
      render: (text: any, record: any) => (<>
        {record.replied} ({`${getPercent(record.replied, record.recipient)}%`})
      </>)
    },
    {
      title: 'interested',
      dataIndex: 'interested',
      key: 'interested',
      align: 'center',
      className: 'capitalize-text',
      render: (text: any, record: any) => (<>
        {record.interested} ({`${getPercent(record.interested, record.recipient)}%`})
      </>)
    },
  ];
}

const OverallStatsContainer = styled.div`
  background-color: #F7F8FA;
  width: 845px;
  margin-top: 16px;
  border-radius: 5px;
`;

const TableTitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  font-style: medium;
`

const StatsBlock = styled.div`
  padding: 8px;
  margin: 8px;
  display: inline-block;
  background-color: #ffffff;
  border-radius: 5px;
  min-width: 88px;
  text-align: center;
  opacity: 0.9999;
`;

const NumTitle = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #5B5B5B;
  opacity: 0.9999;
`;
const NumLabel = styled.div`
  font-size: 10px;
  font-weight: 400;
  opacity: 0.9999;
`;

const Splitter = styled.div`
  display: inline-block;
  border-right: 1px solid #E0E0E0;
  height: 50px;
  margin: 0 10px
`;

const BarChartWrapper = styled.div`
  width: 900px;
  margin: 18px 10px;
  font-size: 10px;
  font-weight: 400;
  border-radius: 5px;
  text-transform: capitalize;
`;

type SearchCampaignProps = {
  campStats: any
}

const SearchCampaign = (props: SearchCampaignProps) => {
  const { campStats } = props;
  const derivedStats = deriveCampaignData(campStats);

  const renderColorfulLegendText = (value: string, entry: any) => {
    return <span style={{ color: "#5B5B5B" }}>{value}</span>;
  }
  return (<>
    <OverallStatsContainer>
      <StatsBlock style={{backgroundColor: "unset"}}>
        <NumTitle style={{ color: "#308DF8" }}>{prettyCount(derivedStats.total.contacted)}</NumTitle>
        <NumLabel>Contacted</NumLabel>
      </StatsBlock>
      <StatsBlock style={{ backgroundColor: "unset" }}>
        <NumTitle style={{ color: "#308DF8" }}>{prettyCount(derivedStats.total.recipient)}</NumTitle>
        <NumLabel>Recipient</NumLabel>
      </StatsBlock>
      <Splitter/>
      <StatsBlock>
        <NumTitle>{getPercent(derivedStats.total.opened, derivedStats.total.recipient)}%</NumTitle>
        <NumLabel>{derivedStats.total.opened} Opened</NumLabel>
      </StatsBlock>
      <StatsBlock>
        <NumTitle>{getPercent(derivedStats.total.clicked, derivedStats.total.recipient)}%</NumTitle>
        <NumLabel>{derivedStats.total.clicked} Clicked</NumLabel>
      </StatsBlock>
      <StatsBlock>
        <NumTitle>{getPercent(derivedStats.total.replied, derivedStats.total.recipient)}%</NumTitle>
        <NumLabel>{derivedStats.total.replied} Replied</NumLabel>
      </StatsBlock>
      <StatsBlock>
        <NumTitle>{getPercent(derivedStats.total.interested, derivedStats.total.recipient)}%</NumTitle>
        <NumLabel>{derivedStats.total.interested} Interested</NumLabel>
      </StatsBlock>
    </OverallStatsContainer>
    <BarChartWrapper>
      <BarChart
        width={700}
        height={200}
        data={derivedStats.monthly.slice(0, 6) }
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip />
        {/* @ts-ignore */}
        <Legend layout="vetical" formatter={renderColorfulLegendText} verticalAlign="middle" align="right" wrapperStyle={{ position: 'relative', left: '94%', top: '-143px' }}/>
        <Bar dataKey="interested" barSize={30} stackId="a" fill="#6BCA85" />
        <Bar dataKey="replied" barSize={30} stackId="a" fill="rgba(48, 141, 248, 0.8)" />
        <Bar dataKey="clicked" barSize={30} stackId="a" fill="rgba(48, 141, 248, 0.6)" />
        <Bar dataKey="opened" barSize={30} stackId="a" fill="rgba(48, 141, 248, 0.4)" />
        <Bar dataKey="recipient" barSize={30} stackId="a" fill="rgba(48, 141, 248, 0.2)" radius={[2, 2, 0, 0]}/>
      </BarChart>
    </BarChartWrapper>
    <TableTitle>Recent Activity</TableTitle>
    <div className="matches-table-container">
      <Table columns={GetTableColumns()} dataSource={derivedStats.weekly.slice(0,6)} pagination={false} />
    </div>
  </>);
}

export default SearchCampaign;
